import '@colibrio/colibrio-reader-framework/colibrio-polyfill-dom';
import '@colibrio/colibrio-reader-framework/colibrio-polyfill-es6';
import '@colibrio/colibrio-reader-framework/colibrio-polyfill-geometry';
import '@colibrio/colibrio-reader-framework/colibrio-polyfill-webanimation';
import {VanillaReaderEventBus,} from './VanillaReaderEventBus/VanillaReaderEventBus';
import {VanillaReaderAppMain} from './VanillaReaderApp/VanillaReaderAppMain';
import {VanillaServiceWorkerEventName} from './service-worker';
import {
    IVanillaReaderAppEvent_PUBLICATION_LOADED
} from "./VanillaReaderEventBus/IVanillaReaderAppEventTypeMap";
import {VanillaRegionBasedNavigation} from "./VanillaReaderGuidedReadingPlayer/VanillaRegionBasedNavigation";
import {VanillaReaderGuidedReadingPlayer} from "./VanillaReaderGuidedReadingPlayer/VanillaReaderGuidedReadingPlayer";
import {PublicationType} from "@colibrio/colibrio-reader-framework/colibrio-core-publication-base";
import {VanillaReaderUiOptionsPresetNames} from "./VanillaReaderUI/VanillaReaderUiModel";
import {ElevenLabsApiClient} from "./utils/tts/elevenlabs/ElevenLabsApiClient";
import {ElevenLabsTtsServiceProvider} from "./utils/tts/elevenlabs/ElevenLabsTtsServiceProvider";
import {ElevenLabsSpeechDataCacheStorage} from "./utils/tts/elevenlabs/ElevenLabsSpeechDataCacheStorage";
import {IVanillaTtsServiceProvider} from "./VanillaReader/VanillaReaderModel";

/*
 *
 * # INDEX.TS
 *
 * Hello fellow coder! How nice to see that you found your way here 🙂
 * Please make sure that you read the README file to get an overview of the architecture etc.
 *
 * This file is the entry point for the Vanilla Reader application. This is where it all kicks off.
 *
 * ## SERVICE WORKER
 *
 * I suggest that you learn the Vanilla Reader basics before you dive into the great PWA void. PWAs has many quite complex
 * aspects that are not connected to anything Colibrio related. So one step at a time is my advice. But you do you 😉
 *
 * The Service Worker code is at the bottom of this file.
 *
 */

// The main application logic
let vanillaReaderAppMain: VanillaReaderAppMain;

/**
 *
 * QUERY STRING PARAMETERS
 *
 * */

const locationHash = document.location.hash;
const locationSearch = document.location.search;

// Querystring parameter that acts as a flag to signal when the user has chosen to open a file using "Open with"
// on a PC.
const openFileFromLaunchQueue = locationHash.indexOf('open_file') >= 0;

// If its in PWA mode, we store application data on the device for offline support.
const useDeviceStorage = locationSearch.indexOf('?pwa') >= 0;

// Just a flag that is used later in this file to ignore the `slowNetwork` option.
const forceHighSpeedConnection = locationSearch.indexOf('force_high_speed_connection') >= 0;

// Querystring parameter that is set if the application needs to perform migration of its data to a newer version of
// the database schema. This is parameter is added in the `controllerchange` event listener in the ServiceWorker
// section at the end of this file.
const performMigration = locationHash.indexOf('perform_migration') >= 0;

const hashItemKey = 'custom_tts_apikey'
const useCustomTts = locationSearch.indexOf(hashItemKey) > 0;
let customTtsApiKey = '';

if(useCustomTts) {
    const hashItemKey = 'custom_tts_apikey'
    customTtsApiKey = locationSearch.slice(locationSearch.indexOf(hashItemKey) + hashItemKey.length + 1);
}

// This checks if the user has set their system settings to reduce motion such as UI animations.
const useReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Grab the `publication_url` URL parameter if it is present. If it is present this publication will be opened
// as soon as the `VanillaReader` has "booted up".
const initialPublicationUrl = getPublicationParameterFromUrl();

window.addEventListener('load', async () => {
    return createVanillaReaderApp();
});

/********************************************************************
 *
 *  MAIN APPLICATION SETUP
 *
 *********************************************************************/

async function createVanillaReaderApp(): Promise<void> {
    let customWebSpeechTtsSynthesizer: IVanillaTtsServiceProvider | undefined;

    if(useCustomTts && customTtsApiKey) {
        const elevenLabsCacheStorageClient = await ElevenLabsSpeechDataCacheStorage.open('vr_11labs_cache_');
        const elevenLabsApiClient = new ElevenLabsApiClient({
            apiKey: customTtsApiKey,
            voiceId: '21m00Tcm4TlvDq8ikWAM'
        }, elevenLabsCacheStorageClient);
        customWebSpeechTtsSynthesizer = new ElevenLabsTtsServiceProvider(elevenLabsApiClient);
    }

    vanillaReaderAppMain = new VanillaReaderAppMain(document.getElementById('root')!, licenseApiKey, {
        initialPublicationUrl,
        useDeviceStorage,
        useReducedMotion,
        forceHighSpeedConnection,
        openFileFromLaunchQueue,
        customWebSpeechTtsSynthesizer
    });

    vanillaReaderAppMain.onAppLoaded(() => {

        maybePerformMigrationAndReload().catch(console.warn);

        // Toast app version for easier service worker debugging etc.
        vanillaReaderAppMain.getVanillaReaderUiInstance()?.toaster.toast(`Reader version ${VanillaReaderAppMain.appVersion}`).catch(console.warn);

        // If the `publication_url` query parameter was set we should load a publication from a web server right away.
        if (initialPublicationUrl) {
            if (initialPublicationUrl.indexOf("http") == 0){
                vanillaReaderAppMain.loadPublicationFromUrl(initialPublicationUrl).catch(console.warn);
            } else {
                vanillaReaderAppMain.getVanillaReaderUiInstance()?.toaster.toast(`Loading this publication. Please wait.`).catch(console.warn);

                vanillaReaderAppMain.loadPublicationFromAws(initialPublicationUrl, getInitialLocator()).catch(console.warn);
            }
        } else {
            if (!openFileFromLaunchQueue) {
                // No initial URL to load and no file in the launch queue, so we show the open file dialog.
                //vanillaReaderAppMain.getVanillaReaderUiInstance()?.dialogOpenFile.show();
                vanillaReaderAppMain.getVanillaReaderUiInstance()?.masterplanStart.show();
            }
        }

        VanillaReaderEventBus.addEventListener('PUBLICATION_LOADED',  async (_ev: CustomEvent<IVanillaReaderAppEvent_PUBLICATION_LOADED>)=>{
            const vanillaReader = vanillaReaderAppMain.getVanillaReaderInstance();
            const vanillaReaderPublication = vanillaReader?.vanillaPublication;
            const vanillaReaderUi = vanillaReaderAppMain.getVanillaReaderUiInstance();

            maybeRestoreUiOptions();

            if(vanillaReaderUi && vanillaReader && vanillaReaderPublication && vanillaReaderPublication.format === PublicationType.EPUB  && VanillaRegionBasedNavigation.publicationHasRegionBasedNavigation(vanillaReaderPublication)) {

                vanillaReaderAppMain.getVanillaReaderUiInstance()?.toaster.toast('Publication has Region Based ' +
                    'Navigation support.');

                let vanillaRegionBasedNavigation = await VanillaRegionBasedNavigation.create(vanillaReaderPublication);

                let guidedReadingTimelineData = vanillaRegionBasedNavigation.toGuidedReadingTimelineData();
                vanillaReaderUi.showMenuOnReaderViewClick = false;
                if(guidedReadingTimelineData) {
                    // @ts-ignore
                    window.guidedReadingPlayer = VanillaReaderGuidedReadingPlayer.create(vanillaReaderAppMain, guidedReadingTimelineData);
                    // @ts-ignore
                    window.guidedReadingPlayer.createMaskLayer(document.getElementById('root')!);

                }

            }

            vanillaReaderAppMain.getVanillaReaderUiInstance()?.menuSide.setPublicationTitle(vanillaReaderPublication?.title ?? "Unknown");

        });

        return Promise.resolve();
    });

    await vanillaReaderAppMain._asyncSetup().catch(console.warn);
    return;
}

function getInitialLocator(): string | undefined{
    if (window.location.hash) {
        return window.location.hash.slice(1);
    }
    return;
}

/*
*
* FILE HANDLING API
*
* This API lets a PWA register as handler for specific file formats. This means that the PWA  will show up as an option
* in the "Open with" dialog on a PC, and that it can be set as the default application for formats.
* since your PWA can now act more as a "proper PC application"
*
* This is a fairly new API introduced in June 2022. So far, it is supported in Chromium based browsers such as
* Chrome and Edge on all PC operating systems and on ChromeOS.
*
* Read more at :
* https://web.dev/file-handling/
*
* */

if ('launchQueue' in window) {
    (window as any)['launchQueue'].setConsumer((launchParams: any) => {
        handleIncomingLaunchFiles(launchParams.files).catch(console.warn);
    });
}

// This function is run anytime there is a file that has been opened from the desktop operating system.
async function handleIncomingLaunchFiles(files: FileSystemFileHandle[]): Promise<void> {
    if (files.length > 0) {
        const file: File = await files[0].getFile();
        await createVanillaReaderApp();
        vanillaReaderAppMain.loadPublicationFromFile(file, undefined, useDeviceStorage).catch(console.warn);
    }
}

// This is just a utility function to find and, if found, return the url to a publication that the user wants to load.
function getPublicationParameterFromUrl(): string | undefined {
    let publicationUrlParameterOffset = document.location.search.indexOf('publication_url=');
    let s3UidParameterOffset = document.location.search.indexOf('uid=');
    if (publicationUrlParameterOffset < 0 && s3UidParameterOffset < 0) {
        return;
    }
    
    if (publicationUrlParameterOffset > -1){
        const nextParameterOffset = document.location.search.indexOf('&', publicationUrlParameterOffset) > 0 ?
            document.location.search.indexOf('&', publicationUrlParameterOffset) :
            undefined;
        const urlParameter = document.location.search.slice(publicationUrlParameterOffset, nextParameterOffset);
        return urlParameter.slice(urlParameter.indexOf('=') + 1);
    }

    if (s3UidParameterOffset > -1){
        const nextParameterOffset = document.location.search.indexOf('&', s3UidParameterOffset) > 0 ?
            document.location.search.indexOf('&', s3UidParameterOffset) :
            undefined;
        const urlParameter = document.location.search.slice(s3UidParameterOffset, nextParameterOffset);
        return urlParameter.slice(urlParameter.indexOf('=') + 1);
    }

    return;

}

function maybeRestoreUiOptions():void {
    let optionsPresetData = vanillaReaderAppMain.getActiveUiPresetData();

    if(!optionsPresetData) {
        return;
    }

    if(!optionsPresetData.rollbackUiOptionsData) {
        console.warn(`maybeRestoreUiOptions(), We have stored preset "${optionsPresetData}", but no initial options to rollback to.`);
    }

    // If the reader was closed or refreshed during guided reading, we should restore the options to the ones
    // that was used before the guided reading mode was started.
    if(optionsPresetData.presetName === VanillaReaderUiOptionsPresetNames.GUIDED_READING_MODE) {
        if(optionsPresetData.rollbackVanillaReaderViewOptionsData) {
            let vanillaReader = vanillaReaderAppMain.getVanillaReaderInstance();
            let vanillaReaderUi = vanillaReaderAppMain.getVanillaReaderUiInstance();
            let vanillaReaderViewOptions = optionsPresetData.rollbackVanillaReaderViewOptionsData;
            let colibrioReaderViewOptions = vanillaReaderViewOptions.colibrioReaderViewOptions;

            if(vanillaReaderUi && colibrioReaderViewOptions) {
                let colibrioStyleOptions = colibrioReaderViewOptions?.publicationStyleOptions;
                if(colibrioStyleOptions) {
                    vanillaReader?.vanillaReaderView?.colibrioReaderView.setOptions({publicationStyleOptions: colibrioStyleOptions});
                    if(vanillaReaderViewOptions.activeRendererTypeName) {
                        vanillaReaderUi.setPublicationRenderer(vanillaReaderViewOptions.activeRendererTypeName);
                    }
                    if(vanillaReaderViewOptions.ignoreAspectRatio !== undefined) {
                        vanillaReaderUi.setIgnoreAspectRatio(vanillaReaderViewOptions.ignoreAspectRatio);
                    }
                    if(vanillaReaderViewOptions.useReducedMotion !== undefined) {
                        vanillaReaderUi.setUseReducedMotion(vanillaReaderViewOptions.useReducedMotion);
                    }
                }
            }
        }

        vanillaReaderAppMain.clearActiveUiPresetData();
    }

}

/*
*
* Data migration
*
* If the application has been upgraded to a newer version and the database schema has been changed since last version
* we may need to migrate data. If a migration is necessary or not is decided in the `controllerchange` event listener
* in the ServiceWorker section at the end of this file.
*
* Depending on if any data was affected by the migration, the page may need to reload.
*
* */

async function maybePerformMigrationAndReload(): Promise<void> {

    if (performMigration) {

        let numberOfItemsMigrated = await vanillaReaderAppMain.performDataMigrationFromLocalStorage();

        if (numberOfItemsMigrated > 0) {
            alert('Migration of data has been performed after version update. The application will reload once more.');
            window.location.href = `index.html${useDeviceStorage ? '?pwa=true' : ''}`;
        } else {
            // Remove the perform_migration hash
            window.location.hash = '';
        }
    }
}

/**********************************************************************
 *
 * PWA SERVICE WORKER SETUP
 *
 *********************************************************************/

/* * This section is all about registering and managing updates of the `service-worker.ts` script.
*
* There are entire books just dedicated to Service Workers. They are an awesome technology, byt there are many caveats
* to be aware of, so if you wish to make your reader app progressive, read up on service workers before you start your
* journey.
*
* Have a look at this article at web.dev https://web.dev/offline-cookbook/ and this one specifically about caching
* https://web.dev/service-worker-caching-and-http-caching/
*
* I have not successfully been able to move this code to a separate file.
*
**/

let serviceWorkerUpdated: boolean = false;

// Note the `USE_SERVICEWORKER` constant that acts as a switch to turn on the service worker based on an url parameter.
if ('serviceWorker' in navigator) {
    let updatedServiceWorker: ServiceWorker | null;

    navigator.serviceWorker.register(
        new URL('service-worker.ts', import.meta.url),
        {
            type: 'module',
            scope: '/',
            updateViaCache: 'none',
        },
    ).then((registration: ServiceWorkerRegistration) => {

        registration.addEventListener('updatefound', () => {

            console.log('Update to the installed Service Worker found.');

            // Check if the new service worker is in the process of being installed.
            updatedServiceWorker = registration.installing;

            if (updatedServiceWorker) {

                // Add an event listener so that we get now when the installation is done.
                updatedServiceWorker.addEventListener('statechange', (_ev: Event) => {

                    if (updatedServiceWorker?.state === 'installed' && navigator.serviceWorker.controller) {
                        console.log('Updated Service Worker installed.');
                        //serviceWorkerUpdated = true;

                        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('APP_PWA_INSTALLED', {}));

                        // Send a postMessage to the Service Worker to tell it to skip waiting and set the current page
                        // as controller. This will in turn trigger a `controllerchange` event that is handled elsewhere
                        // in this file.
                        updatedServiceWorker.postMessage({eventName: VanillaServiceWorkerEventName.APP_PWA_SKIP_WAITING_INTENT});
                    }
                });
            } else {
                console.error('setupServiceWorker()', 'Unable to install Service Worker.');
            }

        });

    }).catch(console.error);

    // Add event listener so that we get an alert when the service worker has been set as the controller of the page.
    navigator.serviceWorker.addEventListener('controllerchange', () => {

        // If the service worker is new we reload the page in order to make sure that it is activated.
        if (serviceWorkerUpdated) {

            let latestDataMigrationDateStamp = window.localStorage.getItem('VanillaReaderAppDataMigrationDateStamp');
            console.log('Latest data migration date', latestDataMigrationDateStamp);

            if (vanillaReaderAppMain.getVanillaReaderUiInstance()) {
                //alert('The app has been updated to a new version and will be restarted.');
            }

            if (latestDataMigrationDateStamp) {
                // Migration done, we'll just reload
                window.location.href = `index.html${useDeviceStorage ? '?pwa' : ''}`;
            } else {
                // Reload to perform migration
                window.location.href = `index.html${useDeviceStorage ?
                    '?pwa&#perform_migration' :
                    '?#perform_migration'}`;
            }

        }
    });
}

/********************************************************************
 *
 * COLIBRIO READER FRAMEWORK LICENSE CHECK
 *
 *********************************************************************/

// This line is needed in order to let Parcel insert the license key during the build process.
declare const process: MockedNodeProcess;

// `process.env.LICENSE_API_KEY` is replaced by the license key during Parcels build process.
// Don't worry too much about this at the moment, we have more important things to learn :)
const licenseApiKey = "d1f3435c-30ca-4bae-9788-bd003a033834";

if (!licenseApiKey) {
    showAPIKeyMissing();
}

// Displays a message so that you don't forget to add an API key to the project.
function showAPIKeyMissing(): void {
    document.body.innerHTML = `
            <h1>
            License API Key missing
            </h1>
            <p style="font-size: 16px">
                Please edit the .env file and add your License API Key:
            </p>
            
            <code style="font-size: 16px">LICENSE_API_KEY=&lt;Your API Key&gt;</code>;
            
            <p>
                Then rebuild your project with <code>yarn start</code>            
            </p>
        `;
}


